import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { graphql, useStaticQuery } from "gatsby";
import { find, isEmpty, map, reduce, replace, some, startsWith } from "lodash";

import SectionWrapper from "~/components/layout/SectionWrapper";
import SocialLinks from "~/components/links/SocialLinks";
import ContentContainer from "~/components/layout/ContentContainer";
import Image from "~/components/Image";

import { useTranslationContext } from "~/context/TranslationContext";
import { useLocationContext } from "~/context/LocationContext";

import placeholder from "~/images/favicon.png";

const Bottom = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        max-width: ${({ fullWidth }) => (fullWidth ? "none" : "860px")};
        flex-wrap: nowrap;
        justify-content: flex-end;
    }
`;

const BottomLeft = styled.div`
    flex: 1 0 auto;
`;

const BottomRight = styled.div`
    flex: 0 0 auto;
    align-self: flex-end;
    margin: 0 -15px 15px;
`;

const SocialLinksWrapper = styled.ul`
    display: flex;
    flex: 0 0 auto;
`;

const Date = styled.div`
    font-weight: 400;
    font-size: 2rem;
`;

export const Thumbnail = styled(Image)`
    border-radius: 100%;
    max-width: 75px;

    img {
        display: block;
    }
`;

export const Author = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

export const AuthorDetails = styled.div`
    display: flex;
    flex-direction: column;
`;

const AuthorName = styled.span`
    font-size: 2.4rem;
    font-weight: bold;
`;

const AuthorJobTitle = styled.span`
    font-size: 1.4rem;
`;

const Authors = styled.div`
    margin-bottom: ${({ marginBottom }) => `${marginBottom}px`};
    display: flex;
    gap: 3rem;
    flex-wrap: wrap;
`;

const Wrapper = styled(SectionWrapper)`
    margin-bottom: ${({ marginBottomOverride }) =>
        `calc(${marginBottomOverride || 25}px / 2)`} !important;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        margin-top: ${({ marginTop }) => `${marginTop}px`};
        margin-bottom: ${({ marginBottomOverride }) =>
            `${marginBottomOverride || 25}px`} !important;
    }
`;

const AuthorTemplate = ({ image, name, jobTitle }) => (
    <Author>
        {image ? (
            <Thumbnail {...image} />
        ) : (
            <Thumbnail sourceUrl={placeholder} altText={name} />
        )}
        {name || jobTitle ? (
            <AuthorDetails>
                {name ? <AuthorName>{name}</AuthorName> : null}
                {jobTitle ? <AuthorJobTitle>{jobTitle}</AuthorJobTitle> : null}
            </AuthorDetails>
        ) : null}
    </Author>
);

const useCreateShareLinks = ({ url, title }) => {
    const { allWpTranslatableOptions } = useStaticQuery(graphql`
        query StaticShareLinksQuery {
            allWpTranslatableOptions {
                nodes {
                    languageCode
                    translatableOptionFields {
                        socialShareLinks {
                            icon
                            linkUrl
                            screenReaderText
                        }
                    }
                }
            }
        }
    `);

    const { activeCode } = useTranslationContext();

    const options = find(allWpTranslatableOptions?.nodes, {
        languageCode: activeCode,
    });

    const socialShareLinks =
        options?.translatableOptionFields?.socialShareLinks;

    return map(socialShareLinks, (socialShareLink) => {
        const parsedUrl = replace(socialShareLink.linkUrl, "{{url}}", url);
        const linkUrl = replace(parsedUrl, "{{title}}", title);
        let iconLibrary = "";

        if (startsWith(linkUrl, "mailto:")) {
            iconLibrary = "fas";
        }

        return {
            ...socialShareLink,
            linkUrl,
            iconLibrary,
        };
    });
};

const hasAuthorAttributes = (hostAuthor, guestAuthor) => {
    const host = hostAuthor || [];
    const guest = guestAuthor || [];

    const authors = [...host, ...guest];

    const hasThumbnail = some(authors, (author) => {
        const hostImage = author?.featuredImage?.node;
        const guestImage = author?.thumbnail;
        return hostImage || guestImage;
    });

    const hasName = some(authors, (author) => {
        const hostName = author?.authorAttributes?.authorName;
        const guestName = author?.name;
        return hostName || guestName;
    });

    return { hasThumbnail, hasName };
};

const getMarginTop = (hostAuthor, guestAuthor) => {
    const { hasThumbnail, hasName } = hasAuthorAttributes(
        hostAuthor,
        guestAuthor,
    );

    if (hasThumbnail) return -20;
    if (hasName) return -15;
    return 5;
};

const getMarginBottomAuthors = (hostAuthor, guestAuthor) => {
    const { hasThumbnail, hasName } = hasAuthorAttributes(
        hostAuthor,
        guestAuthor,
    );

    if (hasThumbnail) return 25;
    if (hasName) return 32;
    return 0;
};

const ArticleMetaData = ({
    title = "",
    date = "",
    fullWidth = false,
    articleMetadata = {},
}) => {
    const { location } = useLocationContext();

    const url = `${process.env.GATSBY_SITE_URL}${location.pathname}`;

    const {
        guestAuthor,
        hostAuthor,
        hideSocialShareIconsAndDate,
        hideDateOnly,
        marginBottomOverride = null,
        withPadding = false,
        withBorderBottom = false,
    } = articleMetadata || {};

    const socialShareLinks = useCreateShareLinks({ url, title });

    if (
        isEmpty(guestAuthor) &&
        isEmpty(hostAuthor) &&
        hideSocialShareIconsAndDate
    ) {
        return null;
    }

    return (
        <Wrapper
            noMarginBottom
            element="section"
            withPadding={withPadding}
            withBorderBottom={withBorderBottom}
            marginTop={getMarginTop(hostAuthor, guestAuthor)}
            marginBottomOverride={marginBottomOverride}
        >
            <ContentContainer>
                {guestAuthor || hostAuthor ? (
                    <Authors
                        marginBottom={getMarginBottomAuthors(
                            hostAuthor,
                            guestAuthor,
                        )}
                    >
                        {reduce(
                            hostAuthor,
                            (acc, author) => {
                                const image = author?.featuredImage?.node;
                                const name =
                                    author?.authorAttributes?.authorName;
                                const jobTitle =
                                    author?.authorAttributes?.jobTitle;

                                if (name || jobTitle) {
                                    return [
                                        ...acc,
                                        <AuthorTemplate
                                            key={author.id}
                                            image={image}
                                            name={name}
                                            jobTitle={jobTitle}
                                        />,
                                    ];
                                }

                                return acc;
                            },
                            [],
                        )}
                        {reduce(
                            guestAuthor,
                            (acc, author, i) => {
                                const image = author?.thumbnail;
                                const name = author?.name;
                                const jobTitle = author?.jobTitle;

                                if (name || jobTitle) {
                                    return [
                                        ...acc,
                                        <AuthorTemplate
                                            key={i}
                                            image={image}
                                            name={name}
                                            jobTitle={jobTitle}
                                        />,
                                    ];
                                }

                                return acc;
                            },
                            [],
                        )}
                    </Authors>
                ) : null}
                {!hideSocialShareIconsAndDate ? (
                    <Bottom fullWidth={fullWidth}>
                        {!hideDateOnly ? (
                            <BottomLeft>
                                <Date>{date}</Date>
                            </BottomLeft>
                        ) : null}

                        <BottomRight>
                            <SocialLinksWrapper>
                                <SocialLinks
                                    color="gray"
                                    links={socialShareLinks}
                                />
                            </SocialLinksWrapper>
                        </BottomRight>
                    </Bottom>
                ) : null}
            </ContentContainer>
        </Wrapper>
    );
};

export default ArticleMetaData;

ArticleMetaData.propTypes = {
    title: PropTypes.string,
    date: PropTypes.string,
    fullWidth: PropTypes.bool,
    articleMetadata: PropTypes.object,
};
